import { type ReactElement, useState } from "react"
import { Link } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"

import { AnimatedGroup } from "@/components/ui/animated-group"
import { Button } from "@/components/ui/button"
import { HeroHeader } from "@/components/ui/hero-section-1"
import { Seo } from "@/components/Seo"
import { cn } from "@/lib/utils"
import {
  Check,
  ChevronDown,
  Minus,
  Sparkles,
} from "lucide-react"

// --- Types ---

type Plan = {
  id: string
  name: string
  priceLabel: string
  priceDetail?: string
  description: string
  features: string[]
  ctaLabel: string
  ctaTo: string
  popular?: boolean
  highlight?: string
}

type ComparisonRow = {
  feature: string
  starter: boolean | string
  pro: boolean | string
  hardware: boolean | string
}

type FAQ = {
  question: string
  answer: string
}

// --- Data ---

const plans: Plan[] = [
  {
    id: "starter",
    name: "Starter",
    priceLabel: "$0",
    priceDetail: "per month",
    description: "Perfect for pop-ups and new venues just getting started.",
    features: [
      "Unlimited menus & locations",
      "QR ordering & table management",
      "Standard payment processing",
      "Advanced analytics & reporting",
      "Offline mode resilience",
      "Staff roles & permissions",
      "Email & SMS receipts",
    ],
    ctaLabel: "Start Free Trial",
    ctaTo: "/auth/signup?plan=starter",
  },
  {
    id: "pro",
    name: "Pro",
    priceLabel: "$49",
    priceDetail: "per month",
    description: "For growing teams that need advanced control and insights.",
    popular: true,
    highlight: "Most Popular",
    features: [
      "Everything in Starter",
      "Advanced analytics & reporting",
      "Offline mode resilience",
      "Staff roles & permissions",
      "Inventory management",
      "Priority 24/7 support",
    ],
    ctaLabel: "Get Started",
    ctaTo: "/auth/signup?plan=pro",
  },
  {
    id: "hardware",
    name: "Hardware+",
    priceLabel: "$89",
    priceDetail: "per month",
    description: "The complete package with dedicated hardware included.",
    features: [
      "Everything in Pro",
      "Free terminal included",
      "White-glove onboarding",
      "Lifetime hardware warranty",
      "Dedicated account manager",
    ],
    ctaLabel: "Contact Sales",
    ctaTo: "/auth/signup?plan=hardware",
  },
]

const comparisonData: ComparisonRow[] = [
  { feature: "QR Ordering", starter: true, pro: true, hardware: true },
  { feature: "Payment Processing", starter: true, pro: true, hardware: true },
  { feature: "Kitchen Display System", starter: true, pro: true, hardware: true },
  { feature: "Offline Mode", starter: true, pro: true, hardware: true },
  { feature: "Advanced Analytics", starter: true, pro: true, hardware: true },
  { feature: "Staff Management", starter: true, pro: true, hardware: true },
  { feature: "Hardware Included", starter: false, pro: false, hardware: true },
  { feature: "Dedicated Support", starter: "Standard", pro: "Priority", hardware: "VIP" },
]

const faqs: FAQ[] = [
  {
    question: "Are there any hidden fees?",
    answer: "No hidden fees. We believe in complete transparency. You only pay the monthly subscription and standard payment processing rates.",
  },
  {
    question: "Can I change my plan later?",
    answer: "Absolutely. You can upgrade or downgrade your plan at any time from your dashboard. Changes take effect immediately.",
  },
  {
    question: "What hardware do I need?",
    answer: "Braise works on any modern tablet or computer. For the best experience, we recommend iPads for POS terminals. Hardware+ plan includes a terminal.",
  },
  {
    question: "Is there a free trial?",
    answer: "Yes! All plans come with a 30-day free trial. No credit card required to start exploring.",
  },
]

// --- Components ---

const CheckIcon = () => (
  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-600">
    <Check className="h-3.5 w-3.5" strokeWidth={3} />
  </div>
)

const CrossIcon = () => (
  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-400">
    <Minus className="h-3.5 w-3.5" />
  </div>
)

const PricingPageContent = (): ReactElement => {
  return (
    <>
      <Seo
        title="Pricing | Braise POS"
        description="Simple, transparent pricing for restaurants of all sizes."
        canonicalUrl="https://braisepos.com/pricing"
      />
      <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-100 selection:text-blue-900">
        <HeroHeader />

        <main className="relative z-10">
          {/* Hero Section */}
          <section className="relative overflow-hidden pt-24 pb-20 lg:pt-32 lg:pb-24">
            <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-50 via-white to-white" />

            <div className="container mx-auto px-4 text-center">
              <AnimatedGroup
                variants={{
                  container: {
                    visible: { transition: { staggerChildren: 0.1 } },
                  },
                  item: {
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
                  },
                }}
              >
                <div className="mx-auto mb-6 flex w-fit items-center gap-2 rounded-full border border-blue-100 bg-blue-50/50 px-4 py-1.5 text-sm font-medium text-blue-700 backdrop-blur-sm">
                  <Sparkles className="h-4 w-4" />
                  <span>Simple, transparent pricing</span>
                </div>

                <h1 className="mx-auto max-w-4xl text-5xl font-bold tracking-tight text-slate-900 sm:text-6xl lg:text-7xl">
                  Plans that grow with <br className="hidden sm:block" />
                  <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    your business
                  </span>
                </h1>

                <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-600 sm:text-xl">
                  Start for free, upgrade as you scale. No hidden fees, no long-term contracts.
                  Just powerful tools to run your restaurant.
                </p>
              </AnimatedGroup>
            </div>
          </section>

          {/* Stats Section */}
          <section className="py-12 lg:py-16">
            <div className="container mx-auto px-4">
              <AnimatedGroup
                className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
                variants={{
                  container: {
                    visible: { transition: { staggerChildren: 0.1 } },
                  },
                  item: {
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
                  },
                }}
              >
                {[
                  {
                    label: "Launch horizon",
                    value: "Today → go-live",
                    detail: "Ops teams ship the AI stack in under two weeks.",
                  },
                  {
                    label: "Languages live",
                    value: "12+",
                    detail: "Multi-language prompts for crews and guests out of the box.",
                  },
                  {
                    label: "Signals analyzed daily",
                    value: "28M+",
                    detail: "Menu, demand, and inventory signals fuel every suggestion.",
                  },
                  {
                    label: "Experiments running",
                    value: "400+",
                    detail: "Menu layout, imagery, and copy tests stay on autopilot.",
                  },
                ].map((stat, index) => (
                  <div
                    key={index}
                    className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white/50 p-8 text-center shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-lg hover:shadow-blue-900/5"
                  >
                    <div className="absolute inset-0 -z-10 bg-gradient-to-br from-blue-50/50 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <h3 className="mb-2 text-3xl font-bold tracking-tight text-slate-900 lg:text-4xl">
                      <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                        {stat.value}
                      </span>
                    </h3>
                    <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                      {stat.label}
                    </p>
                    <p className="text-sm leading-relaxed text-slate-600">
                      {stat.detail}
                    </p>
                  </div>
                ))}
              </AnimatedGroup>
            </div>
          </section>

          {/* Pricing Cards */}
          <section className="pb-24">
            <div className="container mx-auto px-4">
              <div className="grid gap-8 lg:grid-cols-3 lg:gap-12 max-w-7xl mx-auto">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={cn(
                      "relative flex flex-col rounded-3xl border bg-white p-8 transition-all duration-300 hover:shadow-xl",
                      plan.popular
                        ? "border-blue-200 shadow-2xl shadow-blue-900/5 ring-1 ring-blue-200 scale-105 z-10"
                        : "border-slate-200 shadow-lg shadow-slate-900/5 hover:-translate-y-1"
                    )}
                  >
                    {plan.popular && (
                      <div className="absolute -top-5 left-0 right-0 mx-auto w-fit rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-1 text-sm font-bold text-white shadow-md">
                        Most Popular
                      </div>
                    )}

                    <div className="mb-8">
                      <h3 className="text-xl font-semibold text-slate-900">{plan.name}</h3>
                      <div className="mt-4 flex items-baseline gap-1">
                        <span className="text-5xl font-bold tracking-tight text-slate-900">
                          {plan.priceLabel}
                        </span>
                        {plan.priceDetail && (
                          <span className="text-slate-500">{plan.priceDetail}</span>
                        )}
                      </div>
                      <p className="mt-4 text-slate-600 leading-relaxed">{plan.description}</p>
                    </div>

                    <div className="mb-8 flex-1">
                      <ul className="space-y-4">
                        {plan.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-3 text-slate-700">
                            <CheckIcon />
                            <span className="text-sm font-medium">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Button
                      asChild
                      size="lg"
                      variant={plan.popular ? "default" : "outline"}
                      className="w-full"
                    >
                      <Link to={plan.ctaTo}>{plan.ctaLabel}</Link>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Feature Comparison */}
          <section className="py-24 bg-slate-50/50">
            <div className="container mx-auto px-4 max-w-5xl">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">Compare Plans</h2>
                <p className="mt-4 text-lg text-slate-600">Detailed breakdown of what's included.</p>
              </div>

              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/50">
                        <th className="px-8 py-6 text-left text-sm font-semibold text-slate-500 uppercase tracking-wider">Features</th>
                        <th className="px-8 py-6 text-center text-lg font-bold text-slate-900">Starter</th>
                        <th className="px-8 py-6 text-center text-lg font-bold text-blue-600">Pro</th>
                        <th className="px-8 py-6 text-center text-lg font-bold text-slate-900">Hardware+</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {comparisonData.map((row, i) => (
                        <tr key={i} className="group hover:bg-slate-50/50 transition-colors">
                          <td className="px-8 py-5 text-sm font-medium text-slate-700">{row.feature}</td>
                          <td className="px-8 py-5 text-center">
                            {typeof row.starter === "boolean" ? (
                              row.starter ? <div className="flex justify-center"><CheckIcon /></div> : <div className="flex justify-center"><CrossIcon /></div>
                            ) : (
                              <span className="text-sm font-medium text-slate-900">{row.starter}</span>
                            )}
                          </td>
                          <td className="px-8 py-5 text-center">
                            {typeof row.pro === "boolean" ? (
                              row.pro ? <div className="flex justify-center"><CheckIcon /></div> : <div className="flex justify-center"><CrossIcon /></div>
                            ) : (
                              <span className="text-sm font-medium text-slate-900">{row.pro}</span>
                            )}
                          </td>
                          <td className="px-8 py-5 text-center">
                            {typeof row.hardware === "boolean" ? (
                              row.hardware ? <div className="flex justify-center"><CheckIcon /></div> : <div className="flex justify-center"><CrossIcon /></div>
                            ) : (
                              <span className="text-sm font-medium text-slate-900">{row.hardware}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-24">
            <div className="container mx-auto px-4 max-w-3xl">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">Frequently Asked Questions</h2>
              </div>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <FaqItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-24 bg-slate-900 text-white overflow-hidden relative">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-900/40 via-slate-900 to-slate-900" />
            <div className="container mx-auto px-4 text-center relative z-10">
              <h2 className="text-3xl font-bold sm:text-4xl mb-6">Ready to transform your restaurant?</h2>
              <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
                Join thousands of venues using Braise to streamline operations and delight guests.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" variant="default">
                  <Link to="/auth/signup">Get Started Now</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-slate-700 bg-transparent text-white hover:bg-slate-800 hover:text-white hover:border-slate-600"
                >
                  <Link to="/contact">Contact Sales</Link>
                </Button>
              </div>
            </div>
          </section>

        </main>
      </div>
    </>
  )
}

const FaqItem = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden transition-all duration-200 hover:border-blue-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between px-6 py-5 text-left"
      >
        <span className="text-lg font-semibold text-slate-900">{question}</span>
        <ChevronDown
          className={cn("h-5 w-5 text-slate-400 transition-transform duration-200", isOpen && "rotate-180")}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-6 pb-6 pt-0 text-slate-600 leading-relaxed">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const PricingPage = (): ReactElement => <PricingPageContent />

export default PricingPage
